import React from 'react';
import { ROUTE_ALIASES } from './data/routeAliases';
import { Routes, Route, Navigate, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PlatformProvider } from './contexts/PlatformContext';
import { ToastProvider, useToast } from './contexts/ToastContext';
import { Toast } from './components/common/Toast';

import LoginPage from './pages/LoginPage';
import OAuthConsentPage from './pages/OAuthConsentPage';
import InviteRegisterPage from './pages/InviteRegisterPage';
import ScoreAnAgentPage from './pages/ScoreAnAgentPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SsoCallbackPage from './pages/SsoCallbackPage';
import DashboardLayout from './pages/DashboardLayout';
// Phase 0 PR #6 — the resolver picks DashboardLayout (v1) vs
// DashboardLayoutV2 (v2) based on features.rebirth.shell.v2. When the
// flag is off the resolver returns the v1 component verbatim, so this
// import is strictly additive.
import ResolvedDashboardLayout from './pages/ResolvedDashboardLayout';
// Phase 1 — OverviewPageV2 is a resolver that picks v2 vs v1 based on
// features.rebirth.overview.v2 (which itself requires features.rebirth.shell.v2).
// When both flags are off, ResolvedOverviewPage returns the original v1
// OverviewPage verbatim, so this swap is strictly additive.
import ResolvedOverviewPage from './pages/OverviewPageV2';
// Phase 3 — Agentlets inventory v2 resolver. v1 AgentsPage rendered when flag off.
import ResolvedAgentsInventoryPage from './pages/AgentsInventoryPageV2';
// Phase 4 — Agentlet Detail v2 resolver. Mounted at /agents/:id.
// When the detail flag is OFF, the resolver redirects to /agents (v1 page).
import ResolvedAgentDetailPage from './pages/AgentDetailPageV2';
// Smart Agent Wizard — the three-column console mounted at /agents/new. The
// left rail is the visitor's four steps; the right lane is the seven proves,
// filling in from GET /api/v1/agents/{id}/proves as the build progresses.
import SmartAgentWizardPage from './pages/SmartAgentWizardPage';
import CanvasPage from './pages/CanvasPage';
import CanvasPageV2 from './pages/CanvasPageV2';
import CanvasPageV3 from './pages/CanvasPageV3';
import CanvasPageV4 from './pages/CanvasPageV4';
import CanvasMobileGate from './components/v2/CanvasMobileGate';
// RC-152c-1 — Form Designer standalone surface. Imported inline
// (not React.lazy) to match the existing canvas/route convention;
// the form designer is small enough that a separate chunk would
// trade boot-time for round-trip-time on the first click from the
// Canvas inspector's "Design form" button. See
// docs/design/FORM_TASK_EVOLUTION.md for the multi-phase plan.
import FormDesignerPage from './pages/forms/FormDesignerPage';
// RC-227 — Unified inbox aggregating Forms + Attestations. The single
// "waiting on me" surface across all task types per operator directive
// 2026-07-04. Previously coexisted with /forms/to-do (RC-161 Forms-only
// inbox) which was retired in the same commit — /forms/to-do the list
// route now redirects here for back-compat; /forms/to-do/:id item fill
// page stays put because it is the notification-email deep-link target.
import MyTasksPage from './pages/MyTasksPage';
import FormFillPage from './pages/forms/FormFillPage';
// RC-160 — Forms · Library (cross-workflow form_task catalogue).
import FormsLibraryPage from './pages/forms/FormsLibraryPage';
// RC-162c — Forms · Designer index (author picker that routes into
// the Designer editor for the chosen form_task).
import FormsDesignerIndexPage from './pages/forms/FormsDesignerIndexPage';
import { isCanvasV3Enabled, isCanvasV4Enabled, isCanvasV4DefaultEnabled, isRebirthShellEnabled, applyRedesignPalette } from './utils/featureFlags';
// V2 design-system playground (Phase 0 of the rebirth migration).
// Mounted only when features.rebirth.shell.v2 is on AND the user is
// SuperAdmin. Internal reference surface — never linked from production
// nav until the rebirth advances past Phase 0.
import V2PlaygroundPage from './pages/V2PlaygroundPage';
// Phase 10 — Administer section v2 resolvers (bundled module)
import {
  ResolvedAdminCenterPage,
  ResolvedTeamPage,
  ResolvedDeploymentsPage,
  ResolvedGatewayApiKeysPage,
  ResolvedGatewayDashboardPage,
} from './pages/AdministerSectionPagesV2';
// Phase 11 — System v2 resolvers (Super Admin only routes)
import {
  ResolvedIntelligenceCenterPage,
  ResolvedFeatureFlagsPage,
} from './pages/SystemSectionPagesV2';
// Cloud Operations (formerly "Operators Console") — Platform Admin + Super Admin surface
// for managing every Prove7 cluster across both Prove7 Cloud and Self-Hosted modes.
// PA sees cross-customer; Super Admin sees their customer-scoped subset.
// Companion design at /agentlets-production/Prove7_Operators_Console_Design.docx
// Renamed from /operators-console → /cloud-operations to disambiguate from the
// legacy ADMINISTER → "Operator Console" (singular) SRE runtime-triage surface.
// W2: split into section layout + sub-pages.
// Renamed: OperatorsConsoleLayout → CloudOperationsLayout (W2.x cleanup; old
// filename retained as re-export shim for forward-compat). The imported
// identifier name is the React component name; we keep `OperatorsConsoleLayout`
// here to minimize churn at the use site, but the underlying file is
// CloudOperationsLayout.jsx.
import OperatorsConsoleLayout from './pages/operators/CloudOperationsLayout';
// Paved-road Deploy surface — replaces the entity-list landing as the default
// /cloud-operations route. Catalog pages still live under /cloud-operations/advanced.
// Per memory: feedback_cloud_ops_paved_road.
import DeployHomePage from './pages/operators/DeployHomePage';
import OrderFormsPage from './pages/operators/OrderFormsPage';
import OperatorsOverviewPage from './pages/operators/OverviewPage';
import CloudAccountsPage from './pages/operators/CloudAccountsPage';
import SystemWorkflowCatalogPage from './pages/operators/SystemWorkflowCatalogPage';
// W2.6 — real pages
import DeploymentsListPage from './pages/operators/DeploymentsListPage';
import DeploymentDetailPage from './pages/operators/DeploymentDetailPage';
import DeploymentTargetsPage from './pages/operators/DeploymentTargetsPage';
import NewDeploymentWizard from './pages/operators/NewDeploymentWizard';
import SlaMatrixPage from './pages/operators/SlaMatrixPage';
// Real Cloud Operations surfaces (replaced PlaceholderPages.jsx exports
// 2026-06-10 — Slice 1 + 2). PlaceholderPages.jsx kept around in case the
// W4 Self-Hosted dispatch protocol needs a stub variant.
import BridgeAgentsPage from './pages/operators/BridgeAgentsPage';
import OperationsLogPage from './pages/operators/OperationsLogPage';
// Phase 9 — Build section v2 resolvers (bundled module)
import {
  ResolvedConnectorsPage,
  ResolvedGelPlatformConnectorsPage,
  ResolvedAgentMarketplacePage,
  ResolvedTemplateCenterPage,
  ResolvedAgenticAppPacksPage,
} from './pages/BuildSectionPagesV2';
// Phase 8 — Skill Center v2 resolver
import ResolvedSkillCenterPage from './pages/SkillCenterPageV2';
// (Team v1 import removed — ResolvedTeamPage above wraps it via Phase 10)
// V169 follow-up (2026-05-07): the standalone /inference page was
// removed because the Agent Inference tab in Admin Center is the only
// supported surface now. The InferencePage component itself stays in
// the codebase — AdminCenterPage imports it locally for its embedded
// tab (see AdminCenterPage.jsx line ~11). Reading the file from there
// is unaffected by this import being gone.
// (AgentMarketplace v1 import removed — ResolvedAgentMarketplacePage above wraps it)
import ConnectorOAuthCallbackPage from './pages/ConnectorOAuthCallbackPage';
// Phase 2 — ApprovalCenterPageV2 resolver. Same additive pattern as Phase 1.
import ResolvedApprovalCenterPage from './pages/ApprovalCenterPageV2';
/* Phase 6 (P0-IDENT) — Identity plane. Six pages compose the section:
 *   /identity                       → landing (three scope cards + fleet KPIs)
 *   /identity/organization          → Org-scope detail (Phase 6D)
 *   /identity/tenants               → Tenant inheritance pane (Phase 6E)
 *   /identity/tenants/:tenantId     → Per-tenant detail (Phase 6G — fixes dead-end)
 *   /identity/agents                → Agent list with SVID state (Phase 6G)
 *   /identity/agents/:agentId       → Agent 7-section detail (Phase 6G)
 * All routes gated minRole="Tenant Admin". The two `:id` routes MUST
 * be declared BEFORE their index siblings so React Router matches the
 * specific path before falling through to the index. */
import IdentityLandingPageV2 from './pages/IdentityLandingPageV2';
import IdentityOrganizationPageV2 from './pages/IdentityOrganizationPageV2';
// V208 / Phase E-1 + E-4 — RBAC v2 new pages.
import CustomersPageV2 from './pages/CustomersPageV2';
import OperatorConsolePageV2 from './pages/OperatorConsolePageV2';
import SecretsManagerPageV2 from './pages/SecretsManagerPageV2';
import IdentityTenantsPageV2 from './pages/IdentityTenantsPageV2';
import IdentityTenantDetailPageV2 from './pages/IdentityTenantDetailPageV2';
import IdentityAgentsPageV2 from './pages/IdentityAgentsPageV2';
import IdentityAgentDetailPageV2 from './pages/IdentityAgentDetailPageV2';
// Stage 3d-frontend — federation peers management page
import IdentityFederationPeersPageV2 from './pages/IdentityFederationPeersPageV2';
// Stage 4-2 — per-instance identity detail page
import IdentityInstanceDetailPageV2 from './pages/IdentityInstanceDetailPageV2';
// Stage 4-4 — federation map
import IdentityFederationMapPageV2 from './pages/IdentityFederationMapPageV2';
// Stage 4-5 — compliance evidence
import IdentityComplianceEvidencePageV2 from './pages/IdentityComplianceEvidencePageV2';
import IdentityConnectedAppsPageV2 from './pages/IdentityConnectedAppsPageV2';
import IdentitySsoPageV2 from './pages/IdentitySsoPageV2';
import IdentitySpireInfraPageV2 from './pages/IdentitySpireInfraPageV2';
// (IntelligenceCenter v1 import removed — ResolvedIntelligenceCenterPage above wraps it)
// (TemplateCenter v1 import removed — ResolvedTemplateCenterPage above wraps it)
// Phase 7 — Policies v2 resolver
import ResolvedPoliciesPage from './pages/PoliciesPageV2';
/* Intent Profiles Catalog v1.0 — Phase 1E.1 (read-only foundation).
 * Dedicated left-nav surface for the new catalog (sibling of Identity in
 * the Operate section). Reads-only at this phase; authoring lands in
 * Phase 1E.2 and the agent-detail bindings list lands in Phase 1E.3.
 * The legacy per-agent inline editor (AgentDetailPageV2 →
 * IntentProfilesEditor) is untouched and remains the runtime source-of-
 * truth until Phase 1D's feature flag flips. */
import IntentProfilesCatalogPageV2 from './pages/IntentProfilesCatalogPageV2';
/* V191 — Multi-Tenancy redesign Phase 5. Replaces /admin/deployments with
 * /admin/multi-tenancy. Legacy deployments page stays reachable via
 * /admin/deployments?legacy=1 during the migration window. */
import MultiTenancyPageV2 from './pages/MultiTenancyPageV2';
/* G119 — wire to v2 dashboard. v1 still reachable via ?legacy=1 */
import ComplianceCenterPage from './pages/ComplianceCenterPageV2';
/* G127 — wire to v2 inventory; v1 reachable via ?legacy=1 */
import AgentTrustCenterPage from './pages/AgentTrustCenterPageV2';
import TrustScoreAddressPage from './pages/TrustScoreAddressPage';
/* G126 — wire to v2 dashboard; v1 reachable via ?legacy=1 */
import GelEnvelopeDashboardPage from './pages/GelEnvelopeDashboardPageV2';
// (GelPlatformConnectors v1 import removed — ResolvedGelPlatformConnectorsPage above wraps it as the GEL tab on the v2 Connectors page)
/* G124 — wire to v2 inventory; v1 reachable via ?legacy=1 */
/* R3.2 — Legacy /gel-agents page retired in favour of the canonical
 * /agents?type=ExteLet inventory + the rich Registration tab on
 * AgentDetailPageV2 (R3.3). Importing GelAgentsRedirect instead so the
 * old URL still resolves (with ?selected= deep-link preservation) but
 * the legacy slideout is no longer mounted. The slideout file
 * (GelExternalAgentsPageV2.jsx) stays on disk for one release cycle as
 * a rollback safety net — it has no live route binding any more. */
import GelAgentsRedirect from './pages/GelAgentsRedirect';
// (GatewayApiKeys v1 import removed — ResolvedGatewayApiKeysPage above wraps it)
// (GatewayDashboard v1 import removed — ResolvedGatewayDashboardPage above wraps it)
// Phase 6 — Governance Center v2 resolver. v1 GovernanceHubPage rendered when off.
import ResolvedGovernanceCenterPage from './pages/GovernanceCenterPageV2';
// Phase 5 — Agent Ownership Accountability Registry.
import AccountabilityRegistryPackPage from './pages/accountability/AccountabilityRegistryPackPage';
// Phase 5 — Discovery v2 resolvers (v1 pages rendered when flag off).
import ResolvedDiscoveryAgentsPage from './pages/DiscoveryAgentsPageV2';
import ResolvedDiscoveryWorkflowsPage from './pages/DiscoveryWorkflowsPageV2';
import DiscoveryHubPage from './pages/DiscoveryHubPage';
import NativeTrustCenterPage from './pages/scoped/NativeTrustCenterPage';
import ExternalTrustCenterPage from './pages/scoped/ExternalTrustCenterPage';
import NativeCompliancePage from './pages/scoped/NativeCompliancePage';
import ExternalCompliancePage from './pages/scoped/ExternalCompliancePage';
import NativeAuditPage from './pages/scoped/NativeAuditPage';
import ExternalAuditPage from './pages/scoped/ExternalAuditPage';
import RoleGuard from './components/common/RoleGuard';
import RequireModuleRoute from './components/v2/RequireModuleRoute';
import WorkflowDetailPage from './pages/WorkflowDetailPage';
/* G121 — wire to v2 inventory; v1 reachable via ?legacy=1 */
import WorkflowAuditPage from './pages/AuditTrailPageV2';
/* G123/G133 — wire to v2 dashboard; v1 reachable via ?legacy=1 */
import WorkflowIntelligencePage from './pages/WorkflowIntelligencePageV2';
// (AdminCenter v1 import removed — ResolvedAdminCenterPage above wraps it)
import { NavigationProvider } from './contexts/NavigationContext';

// Global error boundary — prevents entire app white-screen on render errors
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('[AppErrorBoundary] Caught render error:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 32, maxWidth: 800, margin: '40px auto', fontFamily: 'system-ui, sans-serif' }}>
          <div style={{ background: '#fef2f2', border: '2px solid #fecaca', borderRadius: 12, padding: 24 }}>
            <h2 style={{ color: '#991b1b', fontSize: 18, marginTop: 0, marginBottom: 12 }}>
              Something went wrong
            </h2>
            <p style={{ color: '#7f1d1d', fontSize: 13, marginBottom: 16 }}>
              A rendering error occurred. The error details below can help diagnose the issue.
            </p>
            <pre style={{
              fontSize: 12, color: '#991b1b', background: '#fff', padding: 16, borderRadius: 8,
              border: '1px solid #fecaca', overflow: 'auto', maxHeight: 300,
              fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
{this.state.error?.toString()}
{this.state.error?.stack}
            </pre>
            {this.state.errorInfo?.componentStack && (
              <details style={{ marginTop: 12 }}>
                <summary style={{ cursor: 'pointer', fontSize: 12, color: '#991b1b', fontWeight: 600 }}>
                  Component Stack
                </summary>
                <pre style={{
                  fontSize: 11, color: '#7f1d1d', background: '#fff5f5', padding: 12, borderRadius: 6,
                  overflow: 'auto', maxHeight: 200, fontFamily: 'monospace', marginTop: 8,
                }}>
{this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
            <button
              onClick={() => { this.setState({ hasError: false, error: null, errorInfo: null }); window.location.reload(); }}
              style={{
                marginTop: 16, padding: '10px 20px', background: '#dc2626', color: '#fff',
                border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
              }}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/* V191 / V195 — /deployments always redirects to /multi-tenancy.
 *
 * Phase-5 originally kept the V1 Deployments page reachable via
 * /deployments?legacy=1 as a migration safety net. V195 removed that
 * fallback per "no legacy customers yet" — the redesign is the only
 * production surface. The redirect stays for any old bookmarks /
 * deep-links pointing at /deployments. */
function DeploymentsRouteResolver() {
  const navigate = useNavigate();
  const { addToast } = useToast();

  React.useEffect(() => {
    addToast({
      tone: 'info',
      title: 'Deployments moved',
      message: 'Administer → Deployments is now Multi-Tenancy.',
    });
    navigate('/multi-tenancy', { replace: true });
  }, [navigate, addToast]);

  return null;
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    // Shown only on a genuine cold / logged-out load — a returning user's
    // session is restored synchronously (AuthContext), so this no longer
    // flashes between reloads. Branded spinner instead of bare text.
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 14,
          background: '#ffffff',
          color: '#52525b',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        <div
          className="animate-spin"
          style={{
            width: 30,
            height: 30,
            borderRadius: '9999px',
            border: '3px solid #e4e4e7',
            borderTopColor: '#4f46e5',
          }}
        />
        <div style={{ fontSize: 13, letterSpacing: '0.01em' }}>Loading your workspace…</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    /* Carry where they were going. Someone following a Trust Score address from a badge on a
     * vendor's site arrives with a destination in mind; sending them to a bare login loses it and
     * lands them on a workspace home that means nothing to them. LoginPage only honours a single
     * leading slash, so this cannot become an open redirect. */
    const dest = window.location.pathname + window.location.search;
    const returnTo = dest && dest !== '/' ? `?returnTo=${encodeURIComponent(dest)}` : '';
    return <Navigate to={`/login${returnTo}`} replace />;
  }

  return children;
}

function AppRoutes() {
  const location = useLocation();
  // Re-assert the redesign palette on every navigation so a long-lived SPA
  // session — or a tab that booted before the flag was set — can't drift back
  // to the default palette. Idempotent: only touches <html data-redesign>.
  React.useEffect(() => { applyRedesignPalette(); }, [location.pathname]);
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<InviteRegisterPage />} />
      {/* Journey 1, steps 2-3. Deliberately outside ProtectedRoute: a stranger scores an agent
          before they have any reason to give us an account, and the endpoint behind it writes
          nothing, touches no tenant and cannot reach an existing score. The account boundary is
          the NEXT step ("Keep this score"), which is where it belongs. */}
      {/* The Smart Agent Wizard is its own surface: authenticated, but rendered
          without the dashboard shell so a first-time visitor meets three columns
          and nothing else. It carries its own link across to the full platform.
          Declared here rather than inside the protected tree because everything
          in there is wrapped by ResolvedDashboardLayout. */}
      <Route path="/agents/smartwizard" element={
        <ProtectedRoute><SmartAgentWizardPage /></ProtectedRoute>
      } />
      <Route path="/score" element={<ScoreAnAgentPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/sso/callback" element={<SsoCallbackPage />} />
      <Route path="/connectors/oauth/callback" element={<ConnectorOAuthCallbackPage />} />
      <Route path="/oauth/consent" element={<OAuthConsentPage />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <NavigationProvider>
            <ResolvedDashboardLayout>
              <RequireModuleRoute>
              <Routes>
                <Route path="/" element={<ResolvedOverviewPage />} />
                <Route path="overview" element={<ResolvedOverviewPage />} />
                {/* Phase 4 — /agents/:id is the v2 Agentlet Detail page.
                    When the detail flag is OFF the resolver redirects to
                    /agents (v1 page with selector). The route MUST be
                    declared BEFORE /agents so the param matcher takes
                    precedence over the index route.
                    DEF-FE-ROUTE-AGENT-DETAIL-MINROLE-001 — dropped
                    `minRole="Developer"` from both /agents/:id and
                    /agents. The agent detail page hosts surfaces that
                    belong to multiple V208 roles (IntentProfilesEditor
                    for Compliance, SPIFFE panel + intent invoke for
                    Auditor read-only review, etc.). Gate purely on
                    `agent.read`; per-section authoring buttons are
                    individually gated by their own perm checks within
                    the page. This unblocks Compliance and Auditor
                    while preserving the agent.read perm boundary
                    (Viewer with agent.read still passes — they had
                    read-only ability already). */}
                {/* Kept as a permanent redirect: TemplateCenterPageV2:665 links here,
                    and the path may already be bookmarked. The wizard itself now lives
                    at /agents/smartwizard, outside this shell. */}
                <Route path="agents/new" element={<Navigate to="/agents/smartwizard" replace />} />
                <Route path="agents/:id" element={
                  <RoleGuard requiredPermission="agent.read">
                    <ResolvedAgentDetailPage />
                  </RoleGuard>
                } />
                <Route path="agents" element={
                  <RoleGuard requiredPermission="agent.read">
                    <ResolvedAgentsInventoryPage />
                  </RoleGuard>
                } />
                {/* /canvas — primary Workflow Canvas. Cutover #175: this
                    used to render the legacy CanvasPage and now renders
                    CanvasPageV3 directly. /canvas-v3 stays mounted as a
                    permanent alias so any pinned bookmarks, deep links
                    from the Visual Mapper, and the Feature Flags
                    historical record continue to resolve. The legacy
                    CanvasPage import is retained but unused — kept for
                    a documented rollback window before file deletion.

                    V4 Phase 4e cutover: when `features.canvasV4Default`
                    is ON (default OFF), /canvas renders CanvasPageV4
                    instead of V3. /canvas-v3 always renders V3 regardless
                    so the escape hatch is always available. Pinned
                    bookmarks remain stable through the flip. See
                    docs/CANVAS_V4_PARITY_AUDIT.md for cutover criteria. */}
                <Route path="canvas" element={
                  <RoleGuard requiredPermission="workflow.read" minRole="Developer">
                    <CanvasMobileGate>
                      {isCanvasV4DefaultEnabled() ? <CanvasPageV4 /> : <CanvasPageV3 />}
                    </CanvasMobileGate>
                  </RoleGuard>
                } />
                {/* RC-152c-1 — Form Designer standalone surface.
                    Same RBAC gate as /canvas (workflow.read / Developer
                    minRole) — editing a form_task's schema is an
                    authoring action, same permission tier as editing
                    the canvas itself. Lives INSIDE the dashboard
                    layout (keeps the sidebar) so operators don't
                    lose their navigational anchor when they jump
                    over from the canvas Inspector — same posture as
                    the canvas page. The /forms/design URL is opened
                    from the form_task Inspector's "Design form ↗"
                    button; there is intentionally NO sidebar entry
                    (this is a focused authoring surface, not a
                    top-level destination). */}
                <Route path="forms/design" element={
                  <RoleGuard requiredPermission="workflow.read" minRole="Developer">
                    <FormDesignerPage />
                  </RoleGuard>
                } />
                {/* RC-161 — Forms · To Do (assignee fill surface).
                    Authenticated users can land here regardless of role —
                    any user can be an assignee on a form_task, including
                    Viewer-level accounts. The page itself filters via
                    /api/v1/form-tasks/me which only returns assignments
                    where the current user is in the resolved set. The
                    /forms route landing redirects to /forms/to-do for V1
                    until the Library tab (RC-160) lands. */}
                {/* /forms landing → Library (RC-160 — cross-workflow catalogue).
                    /forms/to-do → assignee inbox (RC-161).
                    /forms/to-do/:id → per-task fill page (RC-161).
                    The Library landing lets ops navigate to any form's
                    Designer without having to remember which workflow it
                    lives in. */}
                {/* RC-162a — Forms RBAC follows Workflows RBAC.
                    /forms (Library landing) + /forms/library are the
                    authored cross-workflow catalogue surfaces — same
                    permission tier as /canvas (workflow.read / Developer
                    minRole). /forms/to-do and /forms/to-do/:id stay
                    OPEN to any authenticated user because any role
                    (including Viewer / Compliance / Auditor) may be an
                    assignee on a form_task and must be able to land on
                    the fill page from a notification email. The page
                    itself filters to the caller's assigned set via
                    /api/v1/form-tasks/me. /forms/design already carries
                    the same Developer-minRole guard. */}
                <Route path="forms" element={
                  <RoleGuard requiredPermission="workflow.read" minRole="Developer">
                    <FormsLibraryPage />
                  </RoleGuard>
                } />
                <Route path="forms/library" element={
                  <RoleGuard requiredPermission="workflow.read" minRole="Developer">
                    <FormsLibraryPage />
                  </RoleGuard>
                } />
                {/* 2026-07-04 — /forms/to-do list route retired per operator
                    directive. It duplicated /my-tasks (RC-227 unified assignee
                    inbox) which the RC-227 sidebar comment already declared
                    the PRIMARY assignee surface. Redirect preserves any
                    outbound notification-email links + operator bookmarks;
                    the item-level fill page /forms/to-do/:id below is
                    UNTOUCHED — that URL is the notification deep-link
                    target and killing it would break in-flight emails. */}
                <Route path="forms/to-do" element={<Navigate to="/my-tasks" replace />} />
                {/* RC-227 — Unified My Tasks inbox. No role gate beyond
                    the parent shell auth: every authenticated user
                    sees their OWN tasks (eligibility-filtered
                    server-side via the assignee resolver). */}
                <Route path="my-tasks" element={<MyTasksPage />} />
                <Route path="forms/to-do/:id" element={<FormFillPage />} />
                {/* RC-162c — Forms · Designer index (picker).
                    Same Developer-minRole gate as /canvas + /forms/design
                    (the editor) per RC-162a's "Forms RBAC mirrors
                    Workflows RBAC" directive. Compliance / Auditor /
                    Viewer hit 403 here, same as on /forms (Library) and
                    /forms/library. */}
                <Route path="forms/designer" element={
                  <RoleGuard requiredPermission="workflow.read" minRole="Developer">
                    <FormsDesignerIndexPage />
                  </RoleGuard>
                } />
                {/* Canvas V2 — @xyflow/react + elkjs hierarchical rewrite.
                    Post task #163 cutover this route is ALWAYS registered so
                    customers can reach it directly at /canvas-v2. The
                    features.canvasV2 flag is kept in featureFlags.js for
                    signalling + historical record (rendered on the Feature
                    Flags page) but no longer gates the route. Kept mounted
                    until V2 customers migrate; a follow-up ticket tracks
                    full V2 retirement. */}
                <Route path="canvas-v2" element={
                  <RoleGuard requiredPermission="workflow.read" minRole="Developer">
                    <CanvasPageV2 />
                  </RoleGuard>
                } />
                {/* /canvas-v3 — permanent alias of /canvas (post-#175
                    cutover). Always registered now so the URL is stable
                    for bookmarks. The features.canvasV3 flag remains in
                    featureFlags.js for signalling/audit but no longer
                    gates the route. */}
                <Route path="canvas-v3" element={
                  <RoleGuard requiredPermission="workflow.read" minRole="Developer">
                    <CanvasPageV3 />
                  </RoleGuard>
                } />
                {/* /canvas-v4 — strangler-fig replacement for V3, ships in
                    phases behind the `features.canvasV4` flag. Mounted only
                    when the flag is enabled so a tenant without the flag set
                    sees a 404 (catch-all) and can't stumble into a Phase-1
                    in-progress surface. V3 is unaffected — V4 lives entirely
                    in parallel until cutover criteria are met (full common-
                    case parity + 2-week internal beta + byte-for-byte save
                    round-trip validated against V3). See pages/CanvasPageV4
                    for the phase progression and CANVAS_V4_DESIGN.md (TBD)
                    for the architectural rationale. */}
                {isCanvasV4Enabled() && (
                  <Route path="canvas-v4" element={
                    <RoleGuard requiredPermission="workflow.read" minRole="Developer">
                      <CanvasPageV4 />
                    </RoleGuard>
                  } />
                )}
                {/* /canvas-legacy — escape hatch to the pre-#175 page,
                    kept for the documented rollback window in case a
                    customer hits a V3 regression we haven't seen.
                    Removed once the rollback window closes. */}
                <Route path="canvas-legacy" element={
                  <RoleGuard requiredPermission="workflow.read" minRole="Developer">
                    <CanvasPage />
                  </RoleGuard>
                } />
                {/* /flags — Platform-Admin-only feature-flag control panel.
                    Unlike canvas-v2 this route is ALWAYS registered (no
                    feature flag gate on it): flags are exactly how an admin
                    would enable canvas-v2 in the first place, so gating
                    /flags behind a flag would be a chicken-and-egg problem.
                    /flags is now the gatekeeper for cluster-wide backend
                    Runtime flags, which are platform-scoped — so only Platform
                    Admin may see it. exactRole pins the gate to exactly
                    Platform Admin (6); since PA sits above Super Admin (5),
                    this admits PA only and never Super Admin (no PA/SA
                    clubbing). */}
                <Route path="flags" element={
                  <RoleGuard exactRole="Platform Admin">
                    <ResolvedFeatureFlagsPage />
                  </RoleGuard>
                } />
                {/* /v2-playground — Phase 0 of the Rebirth migration.
                    Internal reference surface for the v2 design system
                    (Button, Pill, Card, … as they ship). Gated by BOTH:
                      1. features.rebirth.shell.v2 (so the route is
                         unregistered when the flag is off — zero attack
                         surface for production tenants), AND
                      2. SuperAdmin role (RoleGuard inside).
                    Toggling the flag requires a page reload to register
                    the route — this is intentional; keep the cost of
                    enabling explicit. */}
                {isRebirthShellEnabled() && (
                  <Route path="v2-playground" element={
                    <RoleGuard minRole="Super Admin">
                      <V2PlaygroundPage />
                    </RoleGuard>
                  } />
                )}
                <Route path="workflows/:id" element={
                  <RoleGuard requiredPermission="workflow.read" minRole="Developer">
                    <WorkflowDetailPage />
                  </RoleGuard>
                } />
                <Route path="approvals" element={
                  <ResolvedApprovalCenterPage />
                } />
                {/* Phase 6 (P0-IDENT) — Identity plane routes.
                 *
                 * Three pages, all gated minRole="Tenant Admin" because
                 * identity config (trust domains, federation, SVID
                 * rotation policy) is an administrative surface — not
                 * something a Developer or Viewer should reach. The
                 * Trust Center at /native-trust remains the read-only
                 * surface for non-admin viewers.
                 *
                 * Order: /identity/organization and /identity/tenants
                 * MUST come BEFORE /identity (the index/landing route)
                 * so React Router resolves the more-specific path first.
                 * Without this ordering, /identity/organization would
                 * be eaten by the /identity index match and render the
                 * landing page instead. */}
                <Route path="identity/organization" element={
                  <RoleGuard minRole="Tenant Admin">
                    <IdentityOrganizationPageV2 />
                  </RoleGuard>
                } />
                {/* :tenantId BEFORE the index /identity/tenants. Phase 6G. */}
                <Route path="identity/tenants/:tenantId" element={
                  <RoleGuard minRole="Tenant Admin">
                    <IdentityTenantDetailPageV2 />
                  </RoleGuard>
                } />
                <Route path="identity/tenants" element={
                  <RoleGuard minRole="Tenant Admin">
                    <IdentityTenantsPageV2 />
                  </RoleGuard>
                } />
                {/* :agentId BEFORE the index /identity/agents. Phase 6G. */}
                <Route path="identity/agents/:agentId" element={
                  <RoleGuard minRole="Tenant Admin">
                    <IdentityAgentDetailPageV2 />
                  </RoleGuard>
                } />
                <Route path="identity/agents" element={
                  <RoleGuard minRole="Tenant Admin">
                    <IdentityAgentsPageV2 />
                  </RoleGuard>
                } />
                {/* Stage 3d-frontend — federation peers management. */}
                <Route path="identity/federation-peers" element={
                  <RoleGuard minRole="Tenant Admin">
                    <IdentityFederationPeersPageV2 />
                  </RoleGuard>
                } />
                {/* Stage 4-2 — per-instance identity detail page. */}
                <Route path="identity/instances/:instanceId" element={
                  <RoleGuard minRole="Tenant Admin">
                    <IdentityInstanceDetailPageV2 />
                  </RoleGuard>
                } />
                {/* Stage 4-4 — federation map. */}
                <Route path="identity/federation-map" element={
                  <RoleGuard minRole="Tenant Admin">
                    <IdentityFederationMapPageV2 />
                  </RoleGuard>
                } />
                {/* Stage 4-5 — compliance evidence. */}
                <Route path="identity/compliance-evidence" element={
                  <RoleGuard minRole="Tenant Admin">
                    <IdentityComplianceEvidencePageV2 />
                  </RoleGuard>
                } />
                {/* Connected Applications — OAuth client management. BEFORE the /identity index. */}
                <Route path="identity/connected-apps" element={
                  <RoleGuard minRole="Tenant Admin">
                    <IdentityConnectedAppsPageV2 />
                  </RoleGuard>
                } />
                {/* SPIRE infrastructure — SPIFFE topology/trust-domain detail. BEFORE the /identity index. */}
                <Route path="identity/spire-health" element={
                  <RoleGuard minRole="Tenant Admin">
                    <IdentitySpireInfraPageV2 />
                  </RoleGuard>
                } />
                {/* Single sign-on — federated identity providers. BEFORE the /identity index. */}
                <Route path="identity/sso" element={
                  <RoleGuard minRole="Org Admin">
                    <IdentitySsoPageV2 />
                  </RoleGuard>
                } />
                <Route path="identity" element={
                  <RoleGuard minRole="Tenant Admin">
                    <IdentityLandingPageV2 />
                  </RoleGuard>
                } />
                {/* Intent Profiles Catalog v1.0 — Phase 1E.1.
                 * Permission-based gate so Compliance + Auditor + admin tiers
                 * can reach the page. policy.read covers Org/Tenant Admin
                 * + Auditor (V208 added the perm); compliance.admin/policy.admin
                 * cover Compliance role. Super/Platform Admin pass via the
                 * RbacService wildcard. Matches the backend gate on
                 * GET /api/v1/intent-templates exactly. */}
                <Route path="intent-profiles" element={
                  <RoleGuard anyPermission={["policy.read","compliance.admin","policy.admin"]}>
                    <IntentProfilesCatalogPageV2 />
                  </RoleGuard>
                } />
                {/* V191 / Phase-5 — Multi-Tenancy redesign replaces Deployments.
                    /admin/deployments now bounces to /admin/multi-tenancy unless
                    ?legacy=1 is set, which preserves the V1 page during migration. */}
                <Route path="multi-tenancy" element={
                  <RoleGuard requiredPermission="instance.read" minRole="Viewer">
                    <MultiTenancyPageV2 />
                  </RoleGuard>
                } />
                <Route path="deployments" element={
                  <RoleGuard requiredPermission="org.read" minRole="Org Admin">
                    <DeploymentsRouteResolver />
                  </RoleGuard>
                } />
                <Route path="connectors" element={
                  <RoleGuard requiredPermission="connector.create" minRole="Developer">
                    <ResolvedConnectorsPage />
                  </RoleGuard>
                } />
                <Route path="skills" element={
                  <RoleGuard requiredPermission="skill.read" minRole="Developer">
                    <ResolvedSkillCenterPage />
                  </RoleGuard>
                } />
                <Route path="team" element={
                  <RoleGuard requiredPermission="user.manage" minRole="Tenant Admin">
                    <ResolvedTeamPage />
                  </RoleGuard>
                } />
                {/* Secrets Manager (Phase 1) — store credentials in the
                    configured vault, get back vault:// references. Read
                    needs secrets.read (gated server-side too via the
                    SecretController endpoints); Add/Rotate/Delete are
                    additionally gated on secrets.write in the controller. */}
                <Route path="secrets" element={
                  <RoleGuard requiredPermission="secrets.read" minRole="Tenant Admin">
                    <SecretsManagerPageV2 />
                  </RoleGuard>
                } />
                {/* V208 / Phase E-1 — Customer picker (Platform Admin only).
                    RoleGuard uses identity.read as the read gate (PA holds it
                    via wildcard); the page itself + backend further restrict
                    to PA via a hard role check. minRole=Platform Admin would
                    require RoleGuard to know about PA in the role-level chain
                    (it does — Platform Admin = level 5). */}
                <Route path="customers" element={
                  <RoleGuard requiredPermission="identity.read" minRole="Platform Admin">
                    <CustomersPageV2 />
                  </RoleGuard>
                } />
                {/* V208 / Phase E-4 — Operator Console (Operator role + above).
                    Reuses workflow.execute as the action gate; backend RBAC
                    enforces row-scope where it matters. */}
                <Route path="operator-console" element={
                  <RoleGuard requiredPermission="workflow.execute" minRole="Operator">
                    <OperatorConsolePageV2 />
                  </RoleGuard>
                } />
                {/*
                  /inference standalone route deleted 2026-05-07.
                  Admin Center's "Agent Inference" tab is the only
                  supported surface now (AdminCenterPage embeds the
                  InferencePage component directly). Old bookmarks to
                  /inference will hit the wildcard 404; users should be
                  redirected to /admin (Admin Center) — that route
                  already enforces the same Tenant Admin gate.
                */}
                <Route path="agent-marketplace" element={
                  <RoleGuard requiredPermission="agent.read" minRole="Developer">
                    <ResolvedAgentMarketplacePage />
                  </RoleGuard>
                } />
                <Route path="intelligence" element={
                  <RoleGuard minRole="Super Admin">
                    <ResolvedIntelligenceCenterPage />
                  </RoleGuard>
                } />
                <Route path="templates" element={
                  <RoleGuard minRole="Tenant Admin">
                    <ResolvedTemplateCenterPage />
                  </RoleGuard>
                } />
                <Route path="agentic-app-packs" element={
                  <RoleGuard requiredPermission="pack.read" minRole="Developer">
                    <ResolvedAgenticAppPacksPage />
                  </RoleGuard>
                } />
                {/* DEF-RBAC-ROUTE-MINROLE-V208-001 — Policies route guard was
                  * hardcoded minRole="Tenant Admin", which 403'd the Compliance
                  * role from the page Compliance is SPECIFICALLY designed to own.
                  * Per V208 §3 row 9 + CLAUDE_PROJECT_RULES §4.1, Compliance owns
                  * policy.create + policy.edit + policy.publish (Rego authoring).
                  * Permission-based gate now matches the Compliance Center pattern
                  * just below — anyPermission so Compliance + Auditor + TA + OA + SA + PA
                  * can all reach it, while Operator / Developer / Viewer stay denied. */}
                <Route path="policy-config" element={
                  <RoleGuard anyPermission={["policy.read","policy.admin","compliance.admin"]}>
                    <ResolvedPoliciesPage />
                  </RoleGuard>
                } />
                {/* DEF-EXEC-004 — Friendly /policies deep-link.
                  * Email / Slack / runbook links commonly point at /policies
                  * because that's the natural label users type. The actual
                  * page lives at /policy-config (V208 naming); without this
                  * alias the deep-link redirects to / (the wildcard route
                  * handler) and the operator loses context. Reuses the
                  * same RoleGuard so the access contract is identical. */}
                <Route path="policies" element={
                  <RoleGuard anyPermission={["policy.read","policy.admin","compliance.admin"]}>
                    <ResolvedPoliciesPage />
                  </RoleGuard>
                } />
                {/* V208 / Phase E-3 — Compliance Center.
                    Permission-based gate so Compliance + Auditor (level 2)
                    can reach the page. policy.read covers Org Admin /
                    Tenant Admin (V208 added to their perm sets);
                    compliance.admin gates the Compliance role's full
                    write surface. Super/Platform Admin pass via wildcard. */}
                <Route path="compliance" element={
                  <RoleGuard anyPermission={["policy.read","compliance.admin","audit.read"]}>
                    <ComplianceCenterPage />
                  </RoleGuard>
                } />
                {/* DEF-RBAC-ROUTE-MINROLE-V208-001 — Trust Center read access
                  * for Auditor (V208 §3 row 11 grants trust.read + audit.read).
                  * Permission-based gate matches the Compliance Center pattern. */}
                <Route path="trust" element={
                  <RoleGuard anyPermission={["trust.read","audit.read","policy.read"]}>
                    <AgentTrustCenterPage />
                  </RoleGuard>
                } />
                {/* ── Governance Hub (unified cockpit) ── */}
                <Route path="protection-hub" element={
                  <RoleGuard minRole="Tenant Admin"><ResolvedGovernanceCenterPage /></RoleGuard>
                } />
                <Route path="accountability" element={
                  <RoleGuard requiredPermission="audit.read"><AccountabilityRegistryPackPage /></RoleGuard>
                } />

                {/* ── Shadow Agent Discovery ── */}
                <Route path="shadow-discovery" element={
                  <RoleGuard minRole="Tenant Admin"><ResolvedDiscoveryAgentsPage /></RoleGuard>
                } />

                {/* Phase F — Discovery Hub: unified surface (Agents | Signals | Scans | Policies | Settings).
                  * /shadow-discovery preserved for back-compat bookmarks; sidebar nav routes to /discovery. */}
                <Route path="discovery" element={
                  <RoleGuard minRole="Tenant Admin"><DiscoveryHubPage /></RoleGuard>
                } />

                {/* ── Workflow Discovery & GEL Registration ── */}
                <Route path="workflow-discovery" element={
                  <RoleGuard minRole="Tenant Admin"><ResolvedDiscoveryWorkflowsPage /></RoleGuard>
                } />

                {/* ── Workflow Audit (unified across native + external/GEL) ── */}
                {/* V208 / Phase E-5 — Audit Trail re-gated for Auditor role.
                    Permission-based: audit.read covers Auditor + Compliance
                    + Org Admin / Tenant Admin (V208 added the perm).
                    Super/Platform Admin pass via wildcard. gateway.audit.read
                    is included so the Governed Gateway audit subset is
                    reachable without holding the broader audit.read. */}
                <Route path="workflow-audit" element={
                  <RoleGuard anyPermission={["audit.read","gateway.audit.read","compliance.admin"]}>
                    <WorkflowAuditPage />
                  </RoleGuard>
                } />

                {/* ── Workflow Intelligence (analytics across thousands of runs) ── */}
                <Route path="workflow-intelligence" element={
                  <RoleGuard minRole="Tenant Admin"><WorkflowIntelligencePage /></RoleGuard>
                } />

                {/* ── Admin Center (Temporal OSS health + workflow runtime + worker tuning + agent inference) ── */}
                <Route path="admin-center" element={
                  <RoleGuard minRole="Tenant Admin"><ResolvedAdminCenterPage /></RoleGuard>
                } />

                {/* ── Cloud Operations (formerly "Operators Console") — PA + Super
                    Admin surface for managing every Prove7 cluster across both
                    Prove7 Cloud and Self-Hosted modes; first-class home for
                    System Deployment Workflows (workflow.kind=standalone_system).

                    DUAL TENANCY:
                      • Platform Admin sees deployments/targets/cloud-accounts
                        cross-customer (no row-scope filter).
                      • Super Admin sees ONLY their customer's rows (service-layer
                        customer_id filter; row-scope guard on detail endpoints).
                      • READ for PA + SA; WRITE remains PA-only in v1 (SA write
                        access is a follow-up with explicit customer_id binding).

                    Companion docs:
                      • Prove7_Operators_Console_Design.docx  (data model, RBAC, API)
                      • Prove7_Operators_Console_Mockup.html  (visual reference)
                      • Prove7_GKE_Production_Runbook.docx    (source for workflow library)

                    DISAMBIGUATION: this is NOT the legacy ADMINISTER → /operator-console
                    (singular) SRE runtime-triage surface. Distinct routes
                    (/cloud-operations vs /operator-console), distinct purposes
                    (deployment management vs agent kill switch), distinct roles. ── */}
                {/* W2: section layout with sub-routes. The layout renders the
                    subnav + page chrome; each sub-route renders its own page
                    via <Outlet/>. Index redirects to overview so the URL
                    matches what the subnav exposes.
                    Sub-pages live under frontend/src/pages/operators/. */}
                {/* Paved-road landing — mode cards + 4 tabs; replaces the
                    treasure-hunt subnav landing per 2026-06-10 user feedback.
                    Catalog pages still accessible via /cloud-operations/advanced. */}
                <Route path="cloud-operations" element={
                  <RoleGuard minRole="Super Admin"><DeployHomePage /></RoleGuard>
                } />

                {/* Order Forms authoring — Platform Admin only. Feeds the
                    contract-driven deploy picker on /cloud-operations. */}
                <Route path="cloud-operations/order-forms" element={
                  <RoleGuard minRole="Platform Admin"><OrderFormsPage /></RoleGuard>
                } />

                {/* Power-user catalog surface — keeps direct URLs working for
                    bookmark compat. Same subnav as before. */}
                <Route path="cloud-operations/advanced" element={
                  <RoleGuard minRole="Super Admin"><OperatorsConsoleLayout /></RoleGuard>
                }>
                  <Route index element={<Navigate to="overview" replace />} />
                  <Route path="overview"        element={<OperatorsOverviewPage />} />
                  {/* /deployments/new MUST be declared BEFORE /deployments/:id
                      so the literal "new" is matched first. */}
                  <Route path="deployments/new" element={<NewDeploymentWizard />} />
                  <Route path="deployments/:id" element={<DeploymentDetailPage />} />
                  <Route path="deployments"     element={<DeploymentsListPage />} />
                  <Route path="targets"         element={<DeploymentTargetsPage />} />
                  <Route path="cloud-accounts"  element={<CloudAccountsPage />} />
                  <Route path="workflows"       element={<SystemWorkflowCatalogPage />} />
                  <Route path="bridges"         element={<BridgeAgentsPage />} />
                  <Route path="sla"             element={<SlaMatrixPage />} />
                  <Route path="audit"           element={<OperationsLogPage />} />
                </Route>

                {/* ── Agentlets (Native) Agents governance stack ── */}
                <Route path="native-trust" element={
                  <RoleGuard minRole="Tenant Admin"><NativeTrustCenterPage /></RoleGuard>
                } />
                {/* A Trust Score at the address on a badge.
                    Inside the guard on purpose: reaching any agent's score takes a platform login,
                    so a visitor following a badge is sent to register or sign in and returns here.
                    No RoleGuard — the point is that someone who just created an account can read
                    what a vendor published, and the service decides what they may see. */}
                <Route path="t/:slug" element={<TrustScoreAddressPage />} />
                {/* native-compliance → unified Compliance Center with scope */}
                <Route path="native-compliance" element={<Navigate to="/compliance?scope=native" replace />} />
                <Route path="native-audit" element={
                  <RoleGuard minRole="Tenant Admin"><NativeAuditPage /></RoleGuard>
                } />

                {/* ── External Agents governance stack ── */}
                <Route path="external-trust" element={
                  <RoleGuard minRole="Tenant Admin"><ExternalTrustCenterPage /></RoleGuard>
                } />
                {/* external-compliance → unified Compliance Center with scope */}
                <Route path="external-compliance" element={<Navigate to="/compliance?scope=external" replace />} />
                <Route path="external-audit" element={
                  <RoleGuard minRole="Tenant Admin"><ExternalAuditPage /></RoleGuard>
                } />
                {/* R3.2 — /gel-agents is now a thin redirect to the canonical
                 *  /agents?type=ExteLet inventory (or to the specific Agentlet's
                 *  Registration tab when ?selected= or ?agentId= is in the URL).
                 *  RoleGuard still applies — the redirect component itself does
                 *  no privileged work, but Tenant Admin is the right floor for
                 *  the ExteLet surface. */}
                <Route path="gel-agents" element={
                  <RoleGuard minRole="Tenant Admin"><GelAgentsRedirect /></RoleGuard>
                } />
                <Route path="gel-connectors" element={
                  <RoleGuard minRole="Tenant Admin"><ResolvedGelPlatformConnectorsPage /></RoleGuard>
                } />

                {/* ── Governed Gateway ── */}
                <Route path="gateway-keys" element={
                  <RoleGuard minRole="Tenant Admin"><ResolvedGatewayApiKeysPage /></RoleGuard>
                } />
                <Route path="gateway-dashboard" element={
                  <RoleGuard minRole="Tenant Admin"><ResolvedGatewayDashboardPage /></RoleGuard>
                } />

                {/* ── Legacy routes (backwards compat) ── */}
                <Route path="gel-dashboard" element={
                  <RoleGuard minRole="Tenant Admin"><GelEnvelopeDashboardPage /></RoleGuard>
                } />

                {/* P7J-RUN-006/018/022/026/032 — URL-alias redirects.
                 * Sidebar labels imply these paths (e.g. label "Skill Center"
                 * → guess "/skill-center"), but the canonical routes live
                 * elsewhere. Without these aliases the catch-all below
                 * silently dumps the user on "/" with no signal that the URL
                 * was wrong. Aliases preserve typed-URL / bookmark / shared-
                 * link UX without changing the sidebar's canonical hrefs. */}
                {ROUTE_ALIASES.map(({ from, to }) => (
                  <Route key={from} path={from} element={<Navigate to={to} replace />} />
                ))}

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </RequireModuleRoute>
            </ResolvedDashboardLayout>
            </NavigationProvider>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default function App() {
  return (
    <AppErrorBoundary>
      <AuthProvider>
        <PlatformProvider>
          <ToastProvider>
            <AppRoutes />
            <Toast />
          </ToastProvider>
        </PlatformProvider>
      </AuthProvider>
    </AppErrorBoundary>
  );
}
